import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Spinner } from '../components/Spinner';
import { Button } from '../components/ui/button';
import { API_URL } from '../utils/api';
import { orderNumberLabel } from '../utils/format';
import { useCart } from '../context/CartContext';
import { useDocumentTitle } from '../hooks/use-document-title';

const MAX_ATTEMPTS = 6;
const POLL_INTERVAL_MS = 2000;

// Pill buttons: ink for the main action, thin gradient outline for the rest.
const PILL = 'h-11 rounded-full px-5 text-sm font-bold';
const PILL_INK = `${PILL} bg-ink text-paper hover:bg-ink/90`;
const PILL_OUTLINE = `${PILL} gradient-border text-ink shadow-none hover:[--gb-bg:#F2F0EB]`;

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const { refreshCart } = useCart();
  const [status, setStatus] = useState('checking');
  // The same "O00012" the customer hears on the phone and sees in texts and emails.
  const [orderNumbers, setOrderNumbers] = useState([]);
  const timerRef = useRef(null);
  const activeRef = useRef(true);

  const orderId = searchParams.get('order_id');
  const isAdminFree = searchParams.get('admin') === 'true';

  useDocumentTitle(status === 'success' ? 'Order confirmed' : 'Confirming payment');

  const pollPaymentStatus = useCallback(
    async (attempt = 0) => {
      if (!activeRef.current) return;
      if (attempt >= MAX_ATTEMPTS) {
        setStatus('timeout');
        return;
      }
      try {
        const response = await axios.get(`${API_URL}/checkout/status/${orderId}`);
        if (!activeRef.current) return;
        if (Array.isArray(response.data.order_numbers)) setOrderNumbers(response.data.order_numbers);
        if (response.data.status === 'paid') {
          setStatus('success');
          return;
        }
        if (response.data.status === 'expired' || response.data.status === 'cancelled') {
          setStatus('expired');
          return;
        }
        timerRef.current = setTimeout(() => pollPaymentStatus(attempt + 1), POLL_INTERVAL_MS);
      } catch (error) {
        if (activeRef.current) setStatus('error');
      }
    },
    [orderId]
  );

  useEffect(() => {
    activeRef.current = true;
    if (isAdminFree || !orderId) {
      // Admin skip-payment, or the payment API already approved before redirecting here.
      setStatus('success');
    } else {
      setStatus('checking');
      pollPaymentStatus();
    }
    return () => {
      activeRef.current = false;
      clearTimeout(timerRef.current);
    };
  }, [orderId, isAdminFree, pollPaymentStatus]);

  // Shown without polling (admin free checkout, or already approved): look the numbers up once.
  const haveNumbers = orderNumbers.length > 0;
  useEffect(() => {
    if (status !== 'success' || !orderId || haveNumbers) return undefined;
    let cancelled = false;
    axios
      .get(`${API_URL}/checkout/status/${orderId}`)
      .then((response) => {
        if (!cancelled && Array.isArray(response.data.order_numbers)) setOrderNumbers(response.data.order_numbers);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [status, orderId, haveNumbers]);

  // Purchased items leave the cart — refresh the navbar badge.
  useEffect(() => {
    if (status === 'success') refreshCart();
  }, [status, refreshCart]);

  const retry = () => {
    setStatus('checking');
    pollPaymentStatus();
  };

  return (
    <div className="min-h-screen bg-paper">
      <Navbar />
      <main id="main-content" className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 pt-28 pb-16 overflow-hidden">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div className="glow-pink absolute -top-24 -left-24 h-[26rem] w-[26rem] sm:left-[12%] sm:top-[10%] opacity-60" />
          <div className="glow-blue absolute -bottom-32 -right-24 h-[28rem] w-[28rem] sm:right-[10%] sm:bottom-[6%] opacity-60" />
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative w-full text-center max-w-md gradient-border gb-glass backdrop-blur-sm rounded-[28px] shadow-[0_30px_60px_-36px_rgba(26,26,26,0.35)] p-8 sm:p-10"
          aria-live="polite"
        >
          {status === 'checking' && (
            <>
              <Spinner className="mb-6" label="Confirming your payment" />
              <h1 className="font-serif text-3xl font-semibold tracking-tight text-ink mb-3">Confirming your payment…</h1>
              <p className="text-[#4A4640] leading-relaxed">This usually takes just a few seconds. Please don't close this page.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-[#E6EEE3] rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-[#2F4429]" aria-hidden="true" />
              </div>
              <h1 className="font-serif text-3xl font-semibold tracking-tight text-ink mb-3">
                {isAdminFree ? 'Order completed' : 'Payment successful!'}
              </h1>
              {orderNumbers.length > 0 && (
                <p className="font-semibold text-ink mb-3" data-testid="checkout-order-numbers">
                  {orderNumberLabel(orderNumbers)}
                </p>
              )}
              <p className="text-[#4A4640] leading-relaxed mb-8">
                Thank you for your order. Your invitation is now unlocked — open it from your dashboard to download
                the watermark-free version.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button asChild className={PILL_INK}>
                  <Link to="/dashboard" data-testid="go-to-dashboard">Go to My Invitations</Link>
                </Button>
                <Button asChild variant="ghost" className={PILL_OUTLINE}>
                  <Link to="/templates">Browse more templates</Link>
                </Button>
              </div>
            </>
          )}

          {(status === 'error' || status === 'timeout' || status === 'expired') && (
            <>
              <div className="w-20 h-20 bg-[#F6EBD2] rounded-full flex items-center justify-center mx-auto mb-6">
                {status === 'timeout' ? (
                  <Clock className="h-10 w-10 text-[#6B4E0E]" aria-hidden="true" />
                ) : (
                  <AlertTriangle className="h-10 w-10 text-[#6B4E0E]" aria-hidden="true" />
                )}
              </div>
              <h1 className="font-serif text-3xl font-semibold tracking-tight text-ink mb-3">
                {status === 'expired'
                  ? 'This checkout has expired'
                  : status === 'timeout'
                  ? 'Your payment is still processing'
                  : 'We couldn’t confirm your payment'}
              </h1>
              <p className="text-[#4A4640] leading-relaxed mb-8">
                If your card was charged, your invitation will be unlocked shortly. Otherwise, please try again or
                contact support.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {status !== 'expired' && orderId && (
                  <Button onClick={retry} className={PILL_INK}>
                    Check again
                  </Button>
                )}
                <Button asChild variant="ghost" className={PILL_OUTLINE}>
                  <Link to={status === 'expired' ? '/cart' : '/dashboard'}>
                    {status === 'expired' ? 'Back to cart' : 'Go to Dashboard'}
                  </Link>
                </Button>
              </div>
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
}
